import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {IonAlert, IonLoading} from '@ionic/react';
import {StyledButton, StyledSocialButton} from "@components/button/button.style";
import {Camera, CameraResultType, CameraSource} from "@capacitor/camera";
import {changeBase46ToBlob, generateFileName, uploadFile} from "@services/file.service";
import {AndroidSettings, IOSSettings, NativeSettings} from "capacitor-native-settings";
import moment from "moment";
import AddImage from '@assets/images/e-add-blue.svg';
import CloseImage from "@assets/images/e-remove.svg";
import {StyledIonModal, StyledModalContent} from "@components/modal/modal.style";
import {File} from "@models/common";
import FileComponent from "@components/file/file.component";
import {StyledCommonFilesContainer} from "@components/file/file.style";
import {StyledContent} from "../../reportPage/modals/commentsModal.style";

type AuditInconcistencyFilesModalProps = {
    isOpen: boolean;
    files: File[];
    onClose: () => void;
    onSubmit: (files: File[]) => void;
};

const AuditInconcistencyFilesModal: React.FC<AuditInconcistencyFilesModalProps> = (props: AuditInconcistencyFilesModalProps) => {

    const {t} = useTranslation();
    const [showModal, updateShowModal] = useState<boolean>(false);
    const [showPermissionAlert, setShowPermissionAlert] = useState<boolean>(false);
    const [files, setFiles] = useState<File[]>([]);
    const [showLoading, updateShowLoading] = useState(false);

    const handleClose = () => {
        props.onClose();
    };

    const takeSystemPhoto = async () => {
        let hasPermissions = await Camera.checkPermissions();
        if (hasPermissions.camera === 'denied') {
            setShowPermissionAlert(true);
        } else {
            const image = await Camera.getPhoto({
                source: CameraSource.Camera,
                quality: 90,
                allowEditing: false,
                resultType: CameraResultType.Base64,
                width: 1024,
                height: 1024,
            });

            if (image.base64String) {
                saveFile(image.base64String)
            }
        }
    };

    const saveFile = async (base64String: string) => {
        if (base64String) {
            updateShowLoading(true);
            let blob = changeBase46ToBlob(base64String);
            await uploadFile(blob, generateFileName(moment().format('YYYY-MM-DD H:m:s'), null, base64String))
                .then(response => {
                    let tmpFiles:File[] = files;
                    for(let f of response.data.files) {
                        let file = f as File;

                        tmpFiles = [...tmpFiles, file];
                    }

                    setFiles(tmpFiles);
                });

            updateShowLoading(false);
        }
    }

    const removeFile = (file: File) => {
        const tmpFiles = files.filter((f) => file.path !== f.path);
        setFiles(tmpFiles);
    }

    useEffect(() => {
        handleOpen();
        setFiles(props.files);
    }, [props.isOpen, props.files]);

    const handleOpen = () => {
        updateShowModal(props.isOpen);
    };

    const handleSubmit = () => {
        props.onSubmit(files);
    };

    return (
        <StyledIonModal
            isOpen={showModal}
            onDidDismiss={() => {handleClose()}}
            initialBreakpoint={0.99}
            breakpoints={[0, 0.99]}
        >
            <StyledModalContent className="modal-wrap">
                <img src={CloseImage} className="close" onClick={() => handleClose() } />
                <StyledContent className="content full-height">
                    <StyledCommonFilesContainer>
                        <div className="label">{t('auditReport.files.title')}</div>

                        {files && files.length > 0 && files.map((f, key) =>
                            <FileComponent
                                key={'file_'+key}
                                file={f}
                                onRemove={removeFile}
                                preview={false}
                            />
                        )}

                        <StyledSocialButton onClick={takeSystemPhoto}><img className="button-image" src={AddImage} /> {t('auditReport.files.takePhoto')}</StyledSocialButton>
                        <StyledButton onClick={handleSubmit}>{t('auditReport.files.save')}</StyledButton>
                    </StyledCommonFilesContainer>
                </StyledContent>
            </StyledModalContent>

            <IonAlert
                isOpen={showPermissionAlert}
                onDidDismiss={() => setShowPermissionAlert(false)}
                header={t('common.file_permissions.alert')}
                buttons={[
                    {
                        text: t('common.alertCancel'),
                        role: 'cancel',
                        cssClass: 'secondary',
                        handler: () => {
                            setShowPermissionAlert(false);
                        },
                    },
                    {
                        text: t('common.file_permissions.goToAppSettings'),
                        handler: async () => {
                            setShowPermissionAlert(false);
                            NativeSettings.open({
                                optionAndroid: AndroidSettings.ApplicationDetails,
                                optionIOS: IOSSettings.App
                            })
                        }
                    }
                ]}
            />
            <IonLoading onDidDismiss={() => updateShowLoading(false)} isOpen={showLoading}/>
        </StyledIonModal>
    );
};

export default AuditInconcistencyFilesModal;